import { EasmScanConfigurationWebComponent } from './scanConfiguration/easmScanConfiguration';
import { translateFn } from '../utils/translator.helper';
import { GzDialog } from '../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../ui/gzDialog';
import { GzButtonWebComponent } from '../ui/gzButton';
import { ResponseFailReason } from './scanConfiguration/easmScanConfiguration/types';
import { EASMSchedulerEvents } from './scanConfiguration/easmScanConfigurationScheduler/types';
import { store } from '../redux/store';
import { setRefreshWidgets } from '../genericDashboard/redux/dashboards.widgets.slice';

export function openScanConfigurationModal(companyId: string, dependencies: any): void {
  const easmScanConfigurationComponent = new EasmScanConfigurationWebComponent(companyId);
  const bodyContent = document.createElement('div');
  bodyContent.appendChild(easmScanConfigurationComponent);

  easmScanConfigurationComponent.injectDependencies(dependencies);
  easmScanConfigurationComponent.setCompanyId(companyId);
  easmScanConfigurationComponent.start();

  const footerContent = document.createElement('div');
  footerContent.innerHTML = `
    <div id="save-buttons-container" style="display:inline">
      <gz-button id="button-save" type="full" color="primary" style="margin-right: calc(var(--spacer) * 1.5)">
        ${translateFn('Save#preserve state')}
      </gz-button>
      <gz-button id="button-save-scan-now" type="outline" color="primary">
        ${translateFn('Save & scan')}
      </gz-button>
    </div>

    <gz-button id="button-cancel" type="outline" color="secondary" >
      ${translateFn('Cancel')}
    </gz-button>
  `;

  const saveButton = footerContent.querySelector('#button-save') as GzButtonWebComponent;
  const saveScanButton = footerContent.querySelector('#button-save-scan-now') as GzButtonWebComponent;

  saveButton?.addEventListener('click', async () => {
    await handleSave(easmScanConfigurationComponent, saveButton);
    dialog.dismiss();
    store.dispatch(setRefreshWidgets(true));
  });

  saveScanButton?.addEventListener('click', async () => {
    await handleSaveAndScan(easmScanConfigurationComponent, saveScanButton);
    dialog.dismiss();
    store.dispatch(setRefreshWidgets(true));
  });

  footerContent.querySelector('#button-cancel')?.addEventListener('click', () => {
    dialog.dismiss();
  });

  const windowConfigs: GzDialog = {
    headline: translateFn('Scan configuration'),
    bodyContent: bodyContent,
    footerContent: footerContent,
    isOpen: true,
    closableOutsideBox: false,
    isWindow: true,
    overlayZIndex: 9999,
  };

  const dialog = GzDialogWebComponent.buildDialogElement(
    windowConfigs,
    document.querySelector('.mainContainer') as HTMLElement
  );

  dialog.addEventListener(EASMSchedulerEvents.DAY_PICKED, () => {
    disableSaveButtons(saveButton, saveScanButton, false);
  });
  dialog.addEventListener(EASMSchedulerEvents.DAY_PICKER_EMPTY, () => {
    disableSaveButtons(saveButton, saveScanButton, true);
  });
}

function disableSaveButtons(
  saveButton: GzButtonWebComponent,
  saveScanButton: GzButtonWebComponent,
  shouldDisable: boolean
): void {
  saveButton.disabled = shouldDisable;
  saveScanButton.disabled = shouldDisable;
}

async function handleSaveAndScan(
  easmScanConfigurationComponent: EasmScanConfigurationWebComponent,
  saveScanButton: GzButtonWebComponent
): Promise<void> {
  saveScanButton.setLoadingState(true);
  await easmScanConfigurationComponent.controller?.saveConfiguration(true);
  saveScanButton.setLoadingState(false);
}

async function handleSave(
  easmScanConfigurationComponent: EasmScanConfigurationWebComponent,
  saveButton: GzButtonWebComponent
): Promise<void> {
  saveButton.setLoadingState(true);
  await easmScanConfigurationComponent.controller?.saveConfiguration(false);
  saveButton.setLoadingState(false);
}

export function getErrorMessage(
  reason: ResponseFailReason | undefined,
  defaultErrorMessage: string | null = null
): string {
  switch (reason) {
    case ResponseFailReason.REASON_TYPE_INVALID_INPUT:
      return translateFn('Invalid input.');
    case ResponseFailReason.REASON_TYPE_FAILED_OPERATION:
      return translateFn('Failed operation.');
    case ResponseFailReason.REASON_TYPE_NO_ASSETS_FOUND:
      return translateFn('No assets found.');
    case ResponseFailReason.REASON_TYPE_OPERATION_NOT_ALLOWED:
      return translateFn('Operation not allowed.');
    case ResponseFailReason.REASON_TYPE_MAXIMUM_SCANS_REACHED:
      return translateFn('Daily limit reached for on demand scans.');
    case ResponseFailReason.REASON_TYPE_SUSPENDED_COMPANY:
      return translateFn('Operation not allowed on a suspended company.');
    case ResponseFailReason.REASON_TYPE_DUPLICATED_SCAN:
      return translateFn('A scan is already in progress.');
    case ResponseFailReason.REASON_TYPE_DUPLICATED_ASSET:
      return translateFn('Asset already included in the scan configuration.');
    case ResponseFailReason.REASON_TYPE_INCLUDED_ASSET_TYPE_NOT_SUPPORTED:
      return translateFn('Assets of this type cannot be included in scan.');
    case ResponseFailReason.REASON_TYPE_NO_ASSETS_UPDATED:
      return translateFn('Failed to apply changes to the selected assets.');
    case ResponseFailReason.REASON_TYPE_INTERNAL_ERROR:
      return translateFn('Internal error.');
    case ResponseFailReason.REASON_TYPE_NO_LICENSE:
      return translateFn('This feature is unavailable. The license has expired or is no longer valid.');
    default:
      return defaultErrorMessage ? defaultErrorMessage : translateFn('Internal error.');
  }
}
